import React from 'react';
import PropTypes from 'prop-types';
import { toInteger } from 'lodash';
import { withRouter } from 'react-router';
import connect from 'react-redux/lib/connect/connect';

import { PATH } from '../../constants/path';
import { STATIC_ROOT } from '../../constants';
import { SUB_NAV_CATEGORIES } from '../../services/nav/constants';

import { showSubNav, hideSubNav } from '../../services/nav/actions';
import { clearFilter, filterPartnerOnCategory } from '../../services/filter/actions';

import { buildPathPrefix } from '../../utils/directoryUtils';
import { formatCategoryNameForUrl } from '../../services/category/utils';
import { getBilletterieWallUrl } from '../../services/billetterie/utils';

import SvgComponent from '../SvgComponent';
import LocalizedNavLink from '../router/LocalizedNavLink';

// Rang alphabétique d'un libellé parmi les catégories : avant la première qui le suit
const alphabeticalIndex = (categories, label) => {
    const index = categories.findIndex(
        category => category.label.localeCompare(label, 'fr', { sensitivity: 'base' }) > 0
    );
    return index === -1 ? categories.length : index;
};

class CategoriesSubNavComponent extends React.Component {
    showSubNav = subNav => this.props.showSubNav(subNav);
    hideSubNav = () => this.props.hideSubNav();

    onMouseEnterCategories = () => this.showSubNav(SUB_NAV_CATEGORIES);
    onMouseLeaveCategories = () => this.hideSubNav();

    onClickCategory = (e, id) => {
        const { categories, history, onCategoryClick } = this.props;

        // Le filtre est géré ici : on empêche le resetFilter du conteneur parent
        // (.menu-conteneur onClick) d'écraser le filtre que l'on vient de poser.
        e.stopPropagation();

        if (toInteger(id) > 0) {
            const category = categories.filter(curr => toInteger(curr.id) === toInteger(id))[0];

            this.props.filterPartnerOnCategory('directory', Number(category.id));
        } else {
            history.push(`${buildPathPrefix(this.props.locale, false)}/les-plus-populaires`);
            this.props.clearFilter('directory');
        }

        if (onCategoryClick) {
            onCategoryClick();
        }
    };

    // Lien "Toutes les catégories" : renvoie vers la vue par défaut de
    // l'annuaire mixte, sans filtre de catégorie.
    onClickAllCategories = e => {
        e.stopPropagation();
        this.props.clearFilter('directory');

        if (this.props.onCategoryClick) {
            this.props.onCategoryClick();
        }
    };

    getPath = () => {
        const { isOffline, isEbon, isAllOffers } = this.props;

        if (isAllOffers) {
            return PATH.CASHBACK_CATEGORY_PATH;
        } else if (isOffline) {
            return PATH.CASHBACK_OFFLINE_CATEGORY_PATH;
        } else if (isEbon) {
            return PATH.CASHBACK_EBON_CATEGORY_PATH;
        } else {
            return PATH.CASHBACK_ONLINE_CATEGORY_PATH;
        }
    };

    render() {
        const {
            subNav,
            categories,
            id,
            open,
            ariaLabel,
            disableHover,
            isAllOffers,
            ALL_CATEGORIES,
            BILLETTERIE
        } = this.props;

        // `open` (desktop) prend le pas sur l'état Redux partagé `subNav` (fallback mobile),
        // car les deux sous-menus desktop sont montés simultanément.
        const isOpen = open !== undefined ? open : subNav === SUB_NAV_CATEGORIES;

        const items = categories.map((category, i) => {
            return (
                <LocalizedNavLink
                    to={`${this.getPath()}${formatCategoryNameForUrl(category.label)}-${
                        category.id
                    }`}
                    key={i}
                    onClick={e => this.onClickCategory(e, category.id)}>
                    <span className="sous-menu-libelle">
                        <SvgComponent
                            src={`${STATIC_ROOT}/categories/${category.logo}.svg`}
                            aria-hidden="true"
                            focusable="false"
                        />
                        <span>{category.label}</span>
                    </span>
                </LocalizedNavLink>
            );
        });
        if (isAllOffers) {
            // Billetterie à sa place alphabétique ; zone billetterie = autre application, lien hors routeur
            items.splice(
                alphabeticalIndex(categories, BILLETTERIE),
                0,
                <a
                    key="billetterie"
                    href={getBilletterieWallUrl()}
                    className="sous-menu-billetterie">
                    <span className="sous-menu-libelle">
                        <SvgComponent
                            src={`${STATIC_ROOT}/picto-billetterie.svg`}
                            aria-hidden="true"
                            focusable="false"
                        />
                        <span>{BILLETTERIE}</span>
                    </span>
                </a>
            );
        }

        return (
            <div
                className={`sous-menu-rubriques ${isOpen ? 'open' : ''}`}
                id={id || 'sous-menu-rubriques'}
                aria-label={ariaLabel}
                aria-hidden={isOpen ? undefined : 'true'}
                onMouseEnter={disableHover ? undefined : this.onMouseEnterCategories}
                onMouseLeave={disableHover ? undefined : this.onMouseLeaveCategories}
                onMouseDown={this.props.onMouseDown}>
                <div className="conteneur flex-container-h">{items}</div>
                {isAllOffers && (
                    <div className="conteneur sous-menu-toutes-categories">
                        <LocalizedNavLink
                            to={PATH.CASHBACK_MOST_POPULAR}
                            onClick={this.onClickAllCategories}>
                            <span className="sous-menu-libelle">
                                {ALL_CATEGORIES}
                                <SvgComponent
                                    src={`${STATIC_ROOT}/fleche.svg`}
                                    width="6"
                                    height="10"
                                    aria-hidden="true"
                                    focusable="false"
                                />
                            </span>
                        </LocalizedNavLink>
                    </div>
                )}
            </div>
        );
    }
}

CategoriesSubNavComponent.propTypes = {
    categories: PropTypes.array.isRequired,
    subNav: PropTypes.string,
    isOffline: PropTypes.bool,
    isEbon: PropTypes.bool,
    isAllOffers: PropTypes.bool,
    id: PropTypes.string,
    open: PropTypes.bool,
    ariaLabel: PropTypes.string,
    disableHover: PropTypes.bool,
    onCategoryClick: PropTypes.func,
    onMouseDown: PropTypes.func
};

const mapStateToProps = state => ({
    subNav: state.nav.subNav,
    locale: state.i18n.locale,
    ALL_CATEGORIES: state.i18n.feature.header.menu.ALL_CATEGORIES,
    BILLETTERIE: state.i18n.feature.header.menu.BILLETTERIE
});

const mapDispatchToProps = {
    showSubNav,
    hideSubNav,
    filterPartnerOnCategory,
    clearFilter
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CategoriesSubNavComponent));



// WEBPACK FOOTER //
// ./src/components/header/CategoriesSubNavComponent.js